import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SupportChatFab from "./SupportChatFab";
import cn from "classnames";
import React, { useMemo } from "react";
import type { SupportChatAppearance } from "../../../cores/wembiiSupport/interfaces/types";
import {
  SUPPORT_CHAT_RAMIRO_AVATAR,
  SUPPORT_CHAT_TEAM_FACE_2,
  SUPPORT_CHAT_TEAM_FACE_3,
} from "./supportChatTeamAvatars";
import SupportChatHeaderFace from "./SupportChatHeaderFace";
import { pickSupportHeaderAgents } from "./supportChatHeaderAgents";
import {
  resolveSupportChatTheme,
  resolveSupportBrandAvatar,
  resolveSupportMessageAvatar,
} from "./supportChatTheme";
import "./WembiiSupportChat.scss";

interface Props {
  appearance: SupportChatAppearance;
}

export default function SupportChatLivePreview({ appearance }: Props) {
  const theme = resolveSupportChatTheme(appearance);
  const { panelRoundedClass, bubbleRoundedClass } = theme;
  const showQuickButtons =
    appearance.showQuickButtons &&
    (appearance.showCommercialQuickButton ||
      appearance.showTechnicalQuickButton);

  const agentAvatarSrc = resolveSupportMessageAvatar(appearance.avatarType);
  const brandLogoSrc = resolveSupportBrandAvatar(
    appearance.avatarType,
    appearance.avatar
  );
  const previewHeaderAgents = useMemo(
    () =>
      pickSupportHeaderAgents(
        [
          { userId: "1", name: "Ramiro", avatarUrl: SUPPORT_CHAT_RAMIRO_AVATAR },
          { userId: "2", name: "Sofía", avatarUrl: SUPPORT_CHAT_TEAM_FACE_2 },
          { userId: "3", name: "Martín", avatarUrl: SUPPORT_CHAT_TEAM_FACE_3 },
        ],
        "3",
        4
      ),
    []
  );

  return (
    <div
      className={cn("wembii-support-chat-preview", {
        "wembii-support-chat-preview--left": theme.position === "left",
      })}
      style={theme.cssVars}
    >
      <div className="wembii-support-chat-preview__stage">
        <div className="wsc-panel" role="presentation">
          <div className={cn("wsc-panel-inner", panelRoundedClass)}>
            <header className="wsc-header">
              <div className="wsc-header-top">
                <div className="wsc-tabs-wrap">
                  <div className="wsc-tab-pills">
                    <button
                      type="button"
                      className="wsc-tab-btn"
                      data-active="true"
                      tabIndex={-1}
                    >
                      <FontAwesomeIcon icon={["fas", "comment-dots"]} />
                      Conversación
                    </button>
                    <button type="button" className="wsc-tab-btn" tabIndex={-1}>
                      Artículos
                    </button>
                  </div>
                </div>
                <div className="wsc-header-controls">
                  <button type="button" className="wsc-quick-menu-trigger" tabIndex={-1}>
                    Acciones rápidas
                    <FontAwesomeIcon icon={["fas", "chevron-down"]} style={{ fontSize: 11 }} />
                  </button>
                </div>
              </div>

              <div className="wsc-team-stack">
                <div className="wsc-team-stack__faces">
                  {appearance.avatarType === "agent" ? (
                    previewHeaderAgents.map((agent) => (
                      <SupportChatHeaderFace key={agent.userId} agent={agent} />
                    ))
                  ) : (
                    <span className="wsc-face wsc-face--brand" aria-hidden>
                      <img src={brandLogoSrc} alt="" />
                    </span>
                  )}
                </div>
              </div>
              <div className="wsc-headline">{appearance.welcomeTitle}</div>
              <div className="wsc-subline">
                <span className="wsc-status-dot" aria-hidden />
                {appearance.onlineLabel}
              </div>
            </header>

            <section className="wsc-body">
              <div className="wsc-msg-list">
                <div className="wsc-msg-row">
                  <div
                    className={cn("wsc-msg-avatar", {
                      "wsc-msg-avatar--photo": appearance.avatarType === "agent",
                    })}
                  >
                    <img src={agentAvatarSrc} alt="" />
                  </div>
                  <div className={cn("wsc-bubble", bubbleRoundedClass)}>
                    {appearance.showAgentName ? (
                      <div className="wsc-bubble-sender">Equipo Wembii</div>
                    ) : null}
                    {appearance.welcomeMessage}
                    {appearance.showTimestamp ? (
                      <div className="wsc-bubble-time">Ahora mismo</div>
                    ) : null}
                  </div>
                </div>

                {showQuickButtons ? (
                  <div className="wsc-msg-row wsc-msg-row--quick-actions">
                    <span className="wsc-msg-avatar-spacer" aria-hidden />
                    <div className="wsc-welcome-quick-buttons">
                      {appearance.showCommercialQuickButton ? (
                        <button type="button" className="wsc-chip-btn" tabIndex={-1}>
                          {appearance.quickButtonCommercial}
                        </button>
                      ) : null}
                      {appearance.showTechnicalQuickButton ? (
                        <button type="button" className="wsc-chip-btn" tabIndex={-1}>
                          {appearance.quickButtonTechnical}
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                <div className="wsc-msg-row">
                  <div
                    className={cn(
                      "wsc-bubble",
                      "wsc-bubble--out",
                      bubbleRoundedClass
                    )}
                  >
                    Ejemplo de mensaje del usuario
                  </div>
                </div>
              </div>
            </section>

            <footer className="wsc-foot">
              <div className="wsc-composer-shell">
                <div className="wsc-inline-tools">
                  <button type="button" tabIndex={-1} aria-hidden>
                    <FontAwesomeIcon icon={["fas", "face-smile"]} />
                  </button>
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-hidden
                    disabled
                    className="wsc-inline-tools__disabled"
                    title="Adjuntos (próximamente)"
                  >
                    <FontAwesomeIcon icon={["fas", "paperclip"]} />
                  </button>
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-hidden
                    disabled
                    className="wsc-inline-tools__disabled"
                    title="Audio (próximamente)"
                  >
                    <FontAwesomeIcon icon={["fas", "microphone"]} />
                  </button>
                </div>
                <div className="wsc-composer-input-row">
                  <textarea
                    rows={1}
                    readOnly
                    tabIndex={-1}
                    placeholder={appearance.inputPlaceholder}
                    defaultValue=""
                  />
                  <button
                    type="button"
                    className="wsc-send"
                    tabIndex={-1}
                    aria-hidden
                    disabled
                  >
                    <FontAwesomeIcon icon={["fas", "paper-plane"]} />
                  </button>
                </div>
              </div>
            </footer>
          </div>
        </div>

        <SupportChatFab
          open={false}
          preview
          ariaLabel={appearance.widgetTitle}
          onClick={() => undefined}
        />
      </div>
      <p className="wembii-support-chat-preview__caption">
        {appearance.widgetTitle}
      </p>
    </div>
  );
}
